import { AxiosResponse } from 'axios';

import { ApiVersion } from '@/config';
import { ICFSMasterName, IFireKaseTableResponse } from '@/models';
import { INote } from '@/models/note';

import { http } from './http';
import { IGetParams, IPaginatedListResponse } from './types';
import {
  IMasterName,
  IMasterNameOrganization,
  IMasterNamePerson,
  IMasterOrganizationRequest,
  IMasterNameReq,
} from '../models/master.names';

export const MasterNameApi = {
  getMasterNames: (
    params?: IGetParams,
  ): Promise<AxiosResponse<IMasterName[]>> => {
    return http.get(`api/${ApiVersion.V1}/master-names/options`, { params });
  },
  deleteMasterName: (id: string): Promise<AxiosResponse<IMasterName>> => {
    return http.delete(`api/${ApiVersion.V1}/master-names/${id}`);
  },
  createMasterNamePerson: (
    data: IMasterNameReq,
  ): Promise<AxiosResponse<IMasterNamePerson>> => {
    return http.post(`api/${ApiVersion.V1}/master-names/persons`, data);
  },
  createMasterNameOrganization: (
    data: IMasterOrganizationRequest,
  ): Promise<AxiosResponse<IMasterNameOrganization>> => {
    return http.post(`api/${ApiVersion.V1}/master-names/organizations`, data);
  },
  getMasterName: (id: string): Promise<AxiosResponse<IMasterNamePerson>> => {
    return http.get(`api/${ApiVersion.V1}/master-names/persons/${id}`);
  },
  getNameCfsHistory: (id: string): Promise<AxiosResponse<ICFSMasterName[]>> => {
    return http.get(`api/${ApiVersion.V1}/master-names/${id}/cfs-history`);
  },
  updateMasterName: ({
    _id,
    ...data
  }: IMasterNameReq): Promise<AxiosResponse<IMasterName>> => {
    return http.put(`api/${ApiVersion.V1}/master-names/persons/${_id}`, data);
  },
  updateMasterNameOrganization: ({
    _id,
    ...data
  }: IMasterOrganizationRequest): Promise<
    AxiosResponse<IMasterNameOrganization>
  > => {
    return http.put(
      `api/${ApiVersion.V1}/master-names/organizations/${_id}`,
      data,
    );
  },
  getMasterNameNote: (
    id: string,
    params: IGetParams,
  ): Promise<AxiosResponse<INote[]>> => {
    return http.get(`api/${ApiVersion.V1}/master-names/${id}/notes`, {
      params: { ...params },
    });
  },
  getPaginatedMasterNameNotes: (
    id: string,
    params: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<INote>>> => {
    return http.get(`api/${ApiVersion.V1}/master-names/${id}/notes/list`, {
      params: { ...params },
    });
  },
  addMasterNameNote: (
    nameId: string,
    description: string,
  ): Promise<AxiosResponse<INote>> => {
    return http.post(`api/${ApiVersion.V1}/master-names/${nameId}/notes`, {
      description,
    });
  },
  list: async (
    params?: IGetParams,
  ): Promise<AxiosResponse<IPaginatedListResponse<IFireKaseTableResponse>>> => {
    const res = await http.get<IPaginatedListResponse<IFireKaseTableResponse>>(
      `api/${ApiVersion.V2}/fire-kases/list`,
      { params },
    );
    return res;
  },
  deleteMasterNameNote: (
    id: string,
    noteId: string,
  ): Promise<AxiosResponse<IMasterName>> => {
    return http.delete(
      `api/${ApiVersion.V1}/master-names/${id}/notes/${noteId}`,
    );
  },
  updateMasterNameNote: (
    nameId: string,
    noteId: string,
    description: string,
  ): Promise<AxiosResponse<INote>> => {
    return http.put(
      `api/${ApiVersion.V1}/master-names/${nameId}/notes/${noteId}`,
      { description },
    );
  },
  getMasterNameSSN: (id: string): Promise<AxiosResponse<{ ssn: string }>> => {
    return http.get(`api/${ApiVersion.V1}/master-names/persons/${id}/ssn`);
  },
};
